import { createStore, del, get, keys, set } from "idb-keyval";

// A separate IndexedDB store (not localStorage) because it survives a full
// page reload — unlike React/JS memory, which a refresh wipes instantly,
// mid-fetch or not. Items are only removed once the server has confirmed the
// upload, so a queued-but-not-yet-sent (or interrupted) photo is still here
// the next time this page loads and gets retried automatically.
//
// Files are stored as raw ArrayBuffers, not File/Blob objects (a File/Blob
// stored directly in IndexedDB survived the write but came back empty after
// a real page reload on iOS Safari — server rejected it as "no file
// provided"). A File is reconstructed from the buffer + stored metadata
// right before each upload.
//
// Every ID/data pair is read and written one at a time, never in bulk
// (no entries()/getAll() and no Promise.all across many files): loading many
// full-size phone photos into memory simultaneously on a phone caused real
// corruption (fields coming back empty/wrong after a reload) and made the
// UI feel stuck before the first upload even started.
const store = createStore("mariage-uploads", "queue");

export interface QueuedUpload {
  id: string;
  data: ArrayBuffer;
  fileName: string;
  fileType: string;
  uploaderName: string;
}

const EXTENSION_MIME_FALLBACK: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
};

// Belt-and-braces: if `file.type` ever comes back empty (some browsers don't
// always populate it, e.g. for HEIC in odd cases), fall back to guessing from
// the extension rather than sending a request the server will reject outright.
function resolveMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();
  return (ext && EXTENSION_MIME_FALLBACK[ext]) ?? "";
}

function makeId(): string {
  // Timestamp-prefixed so `keys()` sorts chronologically with a plain string
  // sort, no need to read each record's value just to order the queue.
  return `${Date.now()}-${crypto.randomUUID()}`;
}

export async function enqueueUpload(file: File, uploaderName: string): Promise<QueuedUpload> {
  const item: QueuedUpload = {
    id: makeId(),
    data: await file.arrayBuffer(),
    fileName: file.name,
    fileType: resolveMimeType(file),
    uploaderName,
  };
  await set(item.id, item, store);
  return item;
}

export function dequeueUpload(id: string): Promise<void> {
  return del(id, store);
}

export function toFile(item: QueuedUpload): File {
  return new File([item.data], item.fileName, { type: item.fileType });
}

export async function listQueuedIds(): Promise<string[]> {
  const ids = await keys<string>(store);
  return ids.sort();
}

export function getQueuedUpload(id: string): Promise<QueuedUpload | undefined> {
  return get(id, store);
}
